import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Editor from '@monaco-editor/react';
import {
  Code2, Send, FolderTree, MessageSquare, ChevronRight, ChevronDown,
  Download, Smartphone, Tablet, Monitor, Save,
  Sparkles, Loader2, RefreshCw, X, Eye, Check,
  Terminal as TerminalIcon,
  ArrowLeft, PanelLeftClose, PanelLeft, Layers, Copy
} from 'lucide-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
  generateProject, patchProject, parseFilesFromResponse,
  detectFramework, buildPreviewHtml, saveProject
} from '../services/websiteBuilderService';
import { useAuth } from '../contexts/AuthContext';

// ═══════════════════════════════════════════════════════════════════════════════
// FILE ICON RESOLVER
// ═══════════════════════════════════════════════════════════════════════════════
function getFileIcon(filename) {
  const ext = filename.split('.').pop()?.toLowerCase();
  const map = {
    html: '🌐', htm: '🌐',
    css: '🎨', scss: '🎨', less: '🎨',
    js: '⚡', jsx: '⚛️', ts: '💠', tsx: '⚛️',
    json: '📋', md: '📝', txt: '📄',
    png: '🖼️', jpg: '🖼️', jpeg: '🖼️', svg: '🖼️', gif: '🖼️',
    py: '🐍', rb: '💎', go: '🔷', rs: '🦀',
  };
  return map[ext] || '📄';
}

function getMonacoLanguage(filename) {
  const ext = filename.split('.').pop()?.toLowerCase();
  const map = {
    html: 'html', htm: 'html',
    css: 'css', scss: 'scss', less: 'less',
    js: 'javascript', jsx: 'javascript', mjs: 'javascript',
    ts: 'typescript', tsx: 'typescript',
    json: 'json', md: 'markdown',
    py: 'python', rb: 'ruby',
    svg: 'xml', xml: 'xml',
  };
  return map[ext] || 'plaintext';
}

// ═══════════════════════════════════════════════════════════════════════════════
// FILE TREE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
function FileTree({ files, activeFile, onSelectFile, onDeleteFile }) {
  const tree = useMemo(() => {
    const root = { name: '', children: {}, type: 'dir' };
    Object.keys(files).forEach(path => {
      const parts = path.split('/');
      let current = root;
      parts.forEach((part, i) => {
        if (!current.children[part]) {
          current.children[part] = {
            name: part,
            path: parts.slice(0, i + 1).join('/'),
            children: {},
            type: i === parts.length - 1 ? 'file' : 'dir',
          };
        }
        current = current.children[part];
      });
    });
    return root;
  }, [files]);

  return (
    <div className="text-xs select-none">
      {Object.values(tree.children).map(node => (
        <TreeNode
          key={node.path}
          node={node}
          activeFile={activeFile}
          onSelectFile={onSelectFile}
          onDeleteFile={onDeleteFile}
          depth={0}
        />
      ))}
    </div>
  );
}

function TreeNode({ node, activeFile, onSelectFile, onDeleteFile, depth }) {
  const [isOpen, setIsOpen] = useState(depth < 2);

  if (node.type === 'file') {
    return (
      <div
        className={`group flex items-center gap-1.5 px-2 py-1 cursor-pointer rounded-md transition-all duration-150 ${
          activeFile === node.path
            ? 'bg-indigo-50 text-indigo-700 font-medium'
            : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
        }`}
        style={{ paddingLeft: `${depth * 14 + 8}px` }}
        onClick={() => onSelectFile(node.path)}
      >
        <span className="text-[10px] flex-shrink-0">{getFileIcon(node.name)}</span>
        <span className="truncate flex-1">{node.name}</span>
        {onDeleteFile && (
          <button
            onClick={e => { e.stopPropagation(); onDeleteFile(node.path); }}
            className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-opacity p-0.5"
          >
            <X size={10} />
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <div
        className="flex items-center gap-1 px-2 py-1 cursor-pointer text-slate-500 hover:text-slate-800 rounded-md hover:bg-slate-50/80 transition-all duration-150"
        style={{ paddingLeft: `${depth * 14 + 4}px` }}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <span className="text-[10px]">📁</span>
        <span className="font-medium text-[11px]">{node.name}</span>
      </div>
      {isOpen && (
        <div>
          {Object.values(node.children)
            .sort((a, b) => (a.type === 'dir' ? -1 : 1) - (b.type === 'dir' ? -1 : 1) || a.name.localeCompare(b.name))
            .map(child => (
              <TreeNode
                key={child.path}
                node={child}
                activeFile={activeFile}
                onSelectFile={onSelectFile}
                onDeleteFile={onDeleteFile}
                depth={depth + 1}
              />
            ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN WORKSPACE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export function WebsiteBuilderWorkspace() {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  // ─── State ──────────────────────────────────────────────────────────────────
  const [files, setFiles] = useState({});
  const [activeFile, setActiveFile] = useState(null);
  const [openTabs, setOpenTabs] = useState([]);
  const [previewHtml, setPreviewHtml] = useState('');
  const [viewport, setViewport] = useState('desktop');
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarTab, setSidebarTab] = useState('chat');
  const [showTerminal, setShowTerminal] = useState(false);
  const [terminalOutput, setTerminalOutput] = useState([]);
  const [projectName, setProjectName] = useState('Untitled Project');
  const [framework, setFramework] = useState('html');
  const [selectedModel, setSelectedModel] = useState('anthropic/claude-sonnet-4');
  const [previewKey, setPreviewKey] = useState(0);
  const [projectId, setProjectId] = useState(() => crypto.randomUUID());
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const chatEndRef = useRef(null);
  const iframeRef = useRef(null);
  const chatInputRef = useRef(null);

  // ─── Handle initial prompt from ChatboxContainer ────────────────────────────
  useEffect(() => {
    const state = location.state;
    if (state?.selectedModel) {
      setSelectedModel(state.selectedModel);
    }
    if (state?.initialPrompt) {
      setChatInput(state.initialPrompt);
      setTimeout(() => {
        handleSend(state.initialPrompt);
      }, 500);
      window.history.replaceState({}, document.title);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Auto-scroll chat ───────────────────────────────────────────────────────
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, streamingText]);

  // ─── Rebuild preview when files change ──────────────────────────────────────
  useEffect(() => {
    const fw = detectFramework(files);
    setFramework(fw);

    // buildPreviewHtml now handles HTML, React, and Next.js projects
    if (Object.keys(files).length > 0) {
      const html = buildPreviewHtml(files);
      if (html) {
        setPreviewHtml(html);
        setPreviewKey(k => k + 1);
      }
    }
  }, [files]);

  // ─── Open file in editor ────────────────────────────────────────────────────
  const openFile = useCallback((path) => {
    setActiveFile(path);
    setOpenTabs(prev => prev.includes(path) ? prev : [...prev, path]);
  }, []);

  const closeTab = useCallback((path) => {
    setOpenTabs(prev => {
      const next = prev.filter(p => p !== path);
      if (activeFile === path) {
        setActiveFile(next.length > 0 ? next[next.length - 1] : null);
      }
      return next;
    });
  }, [activeFile]);

  const deleteFile = useCallback((path) => {
    setFiles(prev => {
      const next = { ...prev };
      delete next[path];
      return next;
    });
    closeTab(path);
  }, [closeTab]);

  // ─── Editor change handler ──────────────────────────────────────────────────
  const handleEditorChange = useCallback((value) => {
    if (activeFile && value !== undefined) {
      setFiles(prev => ({ ...prev, [activeFile]: value }));
    }
  }, [activeFile]);

  // ─── Send message / generate ────────────────────────────────────────────────
  const handleSend = useCallback(async (overrideMessage) => {
    const msg = (overrideMessage || chatInput).trim();
    if (!msg || isGenerating) return;

    const userMsg = { role: 'user', content: msg };
    setChatMessages(prev => [...prev, userMsg]);
    setChatInput('');
    setIsGenerating(true);
    setStreamingText('');

    const isFirstMessage = Object.keys(files).length === 0;
    const conversationHistory = chatMessages.map(m => ({ role: m.role, content: m.content }));

    try {
      let fullText = '';

      if (isFirstMessage) {
        await generateProject({
          prompt: msg,
          model: selectedModel,
          conversationHistory,
          onChunk: (chunk, accumulated) => {
            fullText = accumulated;
            setStreamingText(accumulated);
          },
          onDone: (text) => { fullText = text; },
          onError: (err) => { throw new Error(err); },
        });
      } else {
        await patchProject({
          instruction: msg,
          files,
          model: selectedModel,
          conversationHistory,
          onChunk: (chunk, accumulated) => {
            fullText = accumulated;
            setStreamingText(accumulated);
          },
          onDone: (text) => { fullText = text; },
          onError: (err) => { throw new Error(err); },
        });
      }

      const parsedFiles = parseFilesFromResponse(fullText);
      const fileCount = Object.keys(parsedFiles).length;

      if (fileCount > 0) {
        if (isFirstMessage) {
          setFiles(parsedFiles);
          const firstFile = Object.keys(parsedFiles).find(p =>
            p.endsWith('.html') || p.endsWith('.tsx') || p.endsWith('.jsx') || p === 'src/App.jsx' || p === 'src/app/page.tsx'
          ) || Object.keys(parsedFiles)[0];
          openFile(firstFile);
        } else {
          setFiles(prev => ({ ...prev, ...parsedFiles }));
          const firstChanged = Object.keys(parsedFiles)[0];
          if (firstChanged) openFile(firstChanged);
        }

        if (isFirstMessage) {
          const words = msg.split(' ').slice(0, 5).join(' ');
          setProjectName(words.length > 35 ? words.substring(0, 35) + '...' : words);
        }

        const aiMsg = {
          role: 'assistant',
          content: isFirstMessage
            ? `✅ Generated ${fileCount} files. Your project is ready!`
            : `✅ Updated ${fileCount} file${fileCount > 1 ? 's' : ''}. Changes applied.`,
          filesChanged: Object.keys(parsedFiles),
        };
        setChatMessages(prev => [...prev, aiMsg]);
        setSidebarTab('files');

        // Auto-save to cloud (fire & forget)
        const allFiles = isFirstMessage ? parsedFiles : { ...files, ...parsedFiles };
        saveToCloud(allFiles, isFirstMessage ? (msg.split(' ').slice(0, 5).join(' ')) : undefined);
      } else {
        const textOnly = fullText.replace(/<FILE:.*?>[\s\S]*?<\/FILE>/g, '').trim();
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: textOnly || 'I generated code but couldn\'t parse the files. Please try again.',
        }]);
      }
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: `❌ Error: ${err.message}` }]);
    } finally {
      setIsGenerating(false);
      setStreamingText('');
    }
  }, [chatInput, chatMessages, files, isGenerating, selectedModel, openFile]);

  // ─── Save to Cloud ──────────────────────────────────────────────────────────
  const saveToCloud = useCallback(async (filesToSave, nameToSave) => {
    const userId = userProfile?.id || userProfile?.email;
    if (!userId) return; // Not logged in

    setIsSaving(true);
    try {
      await saveProject({
        id: projectId,
        name: nameToSave || projectName,
        framework,
        files: filesToSave || files,
        userId,
      });
      setLastSaved(new Date());
    } catch (err) {
      console.error('[WebsiteBuilder] Cloud save failed:', err);
    } finally {
      setIsSaving(false);
    }
  }, [projectId, projectName, framework, files, userProfile]);

  // ─── Download ZIP ───────────────────────────────────────────────────────────
  const downloadZip = useCallback(async () => {
    const zip = new JSZip();
    Object.entries(files).forEach(([path, content]) => { zip.file(path, content); });
    const blob = await zip.generateAsync({ type: 'blob' });
    saveAs(blob, `${projectName.replace(/[^a-zA-Z0-9]/g, '_')}.zip`);
  }, [files, projectName]);

  const copyCurrentFile = useCallback(() => {
    if (activeFile && files[activeFile]) navigator.clipboard.writeText(files[activeFile]);
  }, [activeFile, files]);

  const viewportStyles = {
    desktop: { width: '100%', height: '100%' },
    tablet: { width: '768px', height: '100%', margin: '0 auto' },
    mobile: { width: '375px', height: '100%', margin: '0 auto' },
  };

  const fileCount = Object.keys(files).length;

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════════
  return (
    <div className="h-screen w-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 overflow-hidden">

      {/* ── TOP BAR ──────────────────────────────────────────────────────────── */}
      <div className="h-12 flex-shrink-0 flex items-center justify-between px-4 border-b border-slate-200/60 bg-white/80 backdrop-blur-xl z-30">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-sm">
              <Code2 size={14} className="text-white" />
            </div>
            <span className="text-sm font-semibold text-slate-800 tracking-tight">
              {projectName}
            </span>
            {framework !== 'html' && framework !== 'unknown' && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 font-semibold uppercase tracking-wider border border-indigo-100">
                {framework}
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {/* Viewport toggle */}
          <div className="flex items-center bg-slate-100/80 rounded-lg p-0.5 mr-2">
            {[
              { id: 'desktop', icon: Monitor, label: 'Desktop' },
              { id: 'tablet', icon: Tablet, label: 'Tablet' },
              { id: 'mobile', icon: Smartphone, label: 'Mobile' },
            ].map(v => (
              <button
                key={v.id}
                onClick={() => setViewport(v.id)}
                className={`p-1.5 rounded-md transition-all ${
                  viewport === v.id
                    ? 'bg-white text-indigo-600 shadow-sm'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
                title={v.label}
              >
                <v.icon size={14} />
              </button>
            ))}
          </div>

          <button
            onClick={() => setPreviewKey(k => k + 1)}
            className="p-1.5 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all"
            title="Refresh preview"
          >
            <RefreshCw size={14} />
          </button>

          {/* Save to cloud */}
          <button
            onClick={() => saveToCloud()}
            disabled={fileCount === 0 || isSaving}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 hover:text-indigo-600 hover:bg-indigo-50 border border-slate-200 transition-all disabled:opacity-30"
            title={lastSaved ? `Last saved: ${lastSaved.toLocaleTimeString()}` : 'Save project to cloud'}
          >
            {isSaving ? <Loader2 size={13} className="animate-spin" /> : lastSaved ? <Check size={13} className="text-emerald-500" /> : <Save size={13} />}
            <span>{isSaving ? 'Saving...' : lastSaved ? 'Saved' : 'Save'}</span>
          </button>

          <button
            onClick={downloadZip}
            disabled={fileCount === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm hover:shadow-md transition-all disabled:opacity-30 disabled:bg-slate-300"
          >
            <Download size={13} />
            <span>Export ZIP</span>
          </button>

          {fileCount > 0 && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100">
              <Layers size={11} />
              <span className="text-[10px] font-bold">{fileCount} files</span>
            </div>
          )}
        </div>
      </div>

      {/* ── MAIN WORKSPACE ───────────────────────────────────────────────────── */}
      <div className="flex-1 flex overflow-hidden relative">

        {/* ▌ LEFT SIDEBAR ──────────────────────────────────────────────────── */}
        <AnimatePresence mode="wait">
          {!sidebarCollapsed && (
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: 320, opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="flex-shrink-0 border-r border-slate-200/60 bg-white/70 backdrop-blur-2xl flex flex-col overflow-hidden"
            >
              {/* Sidebar tabs */}
              <div className="flex border-b border-slate-100">
                {[
                  { id: 'chat', icon: MessageSquare, label: 'AI Chat' },
                  { id: 'files', icon: FolderTree, label: 'Files' },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setSidebarTab(tab.id)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-xs font-medium transition-all border-b-2 ${
                      sidebarTab === tab.id
                        ? 'border-indigo-500 text-indigo-600 bg-indigo-50/30'
                        : 'border-transparent text-slate-400 hover:text-slate-600'
                    }`}
                  >
                    <tab.icon size={13} />
                    {tab.label}
                    {tab.id === 'files' && fileCount > 0 && (
                      <span className="text-[9px] bg-indigo-100 text-indigo-600 px-1.5 py-0.5 rounded-full font-bold">{fileCount}</span>
                    )}
                  </button>
                ))}
              </div>

              {/* Sidebar content */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {sidebarTab === 'chat' ? (
                  <>
                    <div className="flex-1 overflow-y-auto p-3 space-y-3">
                      {chatMessages.length === 0 && (
                        <div className="text-center py-6">
                          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center mx-auto mb-3 shadow-sm">
                            <Sparkles size={22} className="text-indigo-500" />
                          </div>
                          <p className="text-sm font-semibold text-slate-700 mb-1">DAGGPT Builder</p>
                          <p className="text-[11px] text-slate-400 leading-relaxed max-w-[220px] mx-auto">
                            Describe any website or app. I'll generate the complete codebase with live preview.
                          </p>
                          <div className="mt-5 space-y-1.5">
                            {[
                              "Modern portfolio with animations",
                              "SaaS landing with pricing cards",
                              "E-commerce product showcase",
                              "Admin dashboard with charts",
                              "Blog with dark/light mode",
                            ].map((suggestion, i) => (
                              <button
                                key={i}
                                onClick={() => { setChatInput(suggestion); chatInputRef.current?.focus(); }}
                                className="block w-full text-left text-[11px] px-3 py-2.5 rounded-xl bg-slate-50/80 hover:bg-indigo-50 text-slate-500 hover:text-indigo-600 transition-all border border-slate-100 hover:border-indigo-200"
                              >
                                <span className="opacity-60 mr-1.5">→</span> {suggestion}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {chatMessages.map((msg, i) => (
                        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-[12px] leading-relaxed ${
                            msg.role === 'user'
                              ? 'bg-indigo-600 text-white rounded-br-md shadow-sm'
                              : 'bg-slate-100/80 text-slate-700 rounded-bl-md border border-slate-200/50'
                          }`}>
                            {msg.content}
                            {msg.filesChanged && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {msg.filesChanged.map(f => (
                                  <button
                                    key={f}
                                    onClick={() => { openFile(f); setSidebarTab('files'); }}
                                    className={`text-[9px] px-2 py-0.5 rounded-md font-mono transition-colors ${
                                      msg.role === 'user'
                                        ? 'bg-white/20 hover:bg-white/30 text-white/90'
                                        : 'bg-indigo-50 hover:bg-indigo-100 text-indigo-600'
                                    }`}
                                  >
                                    {f.split('/').pop()}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}

                      {isGenerating && (
                        <div className="flex justify-start">
                          <div className="max-w-[88%] rounded-2xl rounded-bl-md px-3.5 py-2.5 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100/50">
                            <div className="flex items-center gap-2 text-xs">
                              <div className="relative">
                                <Loader2 size={14} className="animate-spin text-indigo-500" />
                                <div className="absolute inset-0 animate-ping">
                                  <Loader2 size={14} className="text-indigo-300 opacity-30" />
                                </div>
                              </div>
                              <span className="text-indigo-600 font-semibold">
                                {streamingText ? 'Writing code...' : 'Analyzing your request...'}
                              </span>
                            </div>
                            {streamingText && (
                              <div className="mt-2 text-[10px] text-slate-400 font-mono bg-white/60 rounded-lg px-2 py-1 truncate max-w-full border border-slate-200/40">
                                {streamingText.slice(-100)}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div ref={chatEndRef} />
                    </div>

                    {/* Chat input */}
                    <div className="p-3 border-t border-slate-100 bg-white/90">
                      <div className="flex items-end gap-2">
                        <textarea
                          ref={chatInputRef}
                          value={chatInput}
                          onChange={e => setChatInput(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleSend();
                            }
                          }}
                          placeholder={fileCount > 0 ? "Describe a change..." : "What do you want to build?"}
                          rows={2}
                          className="flex-1 resize-none rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5 text-xs text-slate-800 placeholder:text-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 transition-all"
                        />
                        <button
                          onClick={() => handleSend()}
                          disabled={isGenerating || !chatInput.trim()}
                          className="flex-shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 disabled:from-slate-200 disabled:to-slate-300 text-white disabled:text-slate-400 flex items-center justify-center transition-all shadow-md hover:shadow-lg disabled:shadow-none"
                        >
                          {isGenerating ? <Loader2 size={14} className="animate-spin" /> : <Send size={13} />}
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  /* ── File Tree Panel ─── */
                  <div className="flex-1 overflow-y-auto py-2 px-1.5">
                    {fileCount === 0 ? (
                      <div className="text-center py-10 text-xs text-slate-400">
                        <FolderTree size={28} className="mx-auto mb-3 opacity-20" />
                        <p className="font-medium text-slate-500">No files yet</p>
                        <p className="text-[10px] mt-1">Start a chat to generate your project</p>
                      </div>
                    ) : (
                      <FileTree
                        files={files}
                        activeFile={activeFile}
                        onSelectFile={openFile}
                        onDeleteFile={deleteFile}
                      />
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ▌ Sidebar toggle */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute top-1/2 -translate-y-1/2 z-20 w-5 h-12 flex items-center justify-center bg-white border border-slate-200 rounded-r-lg shadow-sm hover:bg-indigo-50 text-slate-400 hover:text-indigo-600 transition-all"
          style={{ left: sidebarCollapsed ? 0 : 320 }}
        >
          {sidebarCollapsed ? <PanelLeft size={11} /> : <PanelLeftClose size={11} />}
        </button>

        {/* ▌ CENTER - CODE EDITOR ──────────────────────────────────────────── */}
        <div className="flex-1 flex flex-col min-w-0 border-r border-slate-200/60">
          {/* Editor tabs */}
          <div className="h-9 flex-shrink-0 flex items-center bg-white/60 border-b border-slate-200/60 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {openTabs.map(path => (
              <div
                key={path}
                onClick={() => setActiveFile(path)}
                className={`group flex items-center gap-1.5 px-3 h-full cursor-pointer text-[11px] border-r border-slate-100/80 transition-all whitespace-nowrap ${
                  activeFile === path
                    ? 'bg-white text-indigo-600 font-medium shadow-[inset_0_-2px_0_0_rgb(99,102,241)]'
                    : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50/60'
                }`}
              >
                <span className="text-[10px]">{getFileIcon(path.split('/').pop())}</span>
                <span>{path.split('/').pop()}</span>
                <button
                  onClick={e => { e.stopPropagation(); closeTab(path); }}
                  className="ml-1 opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500 transition-all"
                >
                  <X size={10} />
                </button>
              </div>
            ))}
            {activeFile && (
              <button
                onClick={copyCurrentFile}
                className="ml-auto mr-2 p-1.5 text-slate-300 hover:text-indigo-500 transition-colors rounded-md hover:bg-indigo-50"
                title="Copy file contents"
              >
                <Copy size={12} />
              </button>
            )}
          </div>

          {/* Editor body */}
          <div className="flex-1 min-h-0">
            {activeFile && files[activeFile] !== undefined ? (
              <Editor
                height="100%"
                language={getMonacoLanguage(activeFile)}
                value={files[activeFile]}
                onChange={handleEditorChange}
                theme="vs-light"
                options={{
                  fontSize: 13,
                  fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
                  fontLigatures: true,
                  minimap: { enabled: false },
                  padding: { top: 12, bottom: 12 },
                  scrollBeyondLastLine: false,
                  lineNumbers: 'on',
                  glyphMargin: false,
                  folding: true,
                  lineDecorationsWidth: 8,
                  lineNumbersMinChars: 3,
                  renderWhitespace: 'none',
                  bracketPairColorization: { enabled: true },
                  smoothScrolling: true,
                  cursorSmoothCaretAnimation: 'on',
                  wordWrap: 'on',
                  tabSize: 2,
                  automaticLayout: true,
                }}
              />
            ) : (
              <div className="h-full flex items-center justify-center bg-gradient-to-br from-white to-slate-50/50">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-100/30 flex items-center justify-center mx-auto mb-4 shadow-sm">
                    <Code2 size={24} className="text-indigo-300" />
                  </div>
                  <p className="text-sm text-slate-400 font-medium">No file selected</p>
                  <p className="text-xs text-slate-300 mt-1">
                    {fileCount > 0 ? 'Select a file from the sidebar' : 'Start a conversation to generate code'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Terminal panel */}
          {showTerminal && (
            <div className="h-40 flex-shrink-0 border-t border-slate-200 bg-[#0d1117] text-green-400 font-mono text-[11px] p-3 overflow-y-auto">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-slate-500">
                  <TerminalIcon size={12} />
                  <span className="text-[10px] font-medium uppercase tracking-wider">Terminal</span>
                </div>
                <button onClick={() => setShowTerminal(false)} className="text-slate-500 hover:text-white transition-colors">
                  <X size={12} />
                </button>
              </div>
              {terminalOutput.map((line, i) => (
                <div key={i} className="leading-relaxed">{line}</div>
              ))}
              {terminalOutput.length === 0 && (
                <span className="text-slate-600 italic">
                  WebContainer terminal - npm output will appear here for React/Next.js projects...
                </span>
              )}
            </div>
          )}
        </div>

        {/* ▌ RIGHT - LIVE PREVIEW ──────────────────────────────────────────── */}
        <div className="flex-1 flex flex-col min-w-0 bg-white">
          {/* Preview header */}
          <div className="h-9 flex-shrink-0 flex items-center justify-between px-3 bg-white/60 backdrop-blur-sm border-b border-slate-200/60">
            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              <Eye size={12} className="text-indigo-400" />
              <span className="font-medium">Live Preview</span>
              {viewport !== 'desktop' && (
                <span className="text-[10px] text-indigo-500 font-bold uppercase bg-indigo-50 px-1.5 py-0.5 rounded">{viewport}</span>
              )}
            </div>
            <button
              onClick={() => setShowTerminal(!showTerminal)}
              className={`p-1.5 rounded-md transition-all ${showTerminal ? 'text-indigo-600 bg-indigo-50' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'}`}
              title="Toggle terminal"
            >
              <TerminalIcon size={12} />
            </button>
          </div>

          {/* Preview content */}
          <div className="flex-1 min-h-0 flex items-start justify-center bg-[#f8f9fb] overflow-auto p-0">
            {previewHtml ? (
              <div style={viewportStyles[viewport]} className="h-full transition-all duration-300 ease-out bg-white">
                {viewport !== 'desktop' && (
                  <div className="bg-slate-800 h-1 rounded-t-lg" />
                )}
                <iframe
                  key={previewKey}
                  ref={iframeRef}
                  srcDoc={previewHtml}
                  title="Preview"
                  className="w-full h-full border-0"
                  sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
                />
              </div>
            ) : (
              <div className="h-full w-full flex items-center justify-center">
                <div className="text-center">
                  <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-slate-100 to-indigo-50 border border-slate-200/40 flex items-center justify-center mx-auto mb-5 shadow-inner">
                    <Monitor size={32} className="text-slate-300" />
                  </div>
                  <p className="text-sm text-slate-400 font-semibold">Live Preview</p>
                  <p className="text-xs text-slate-300 mt-1 max-w-[220px] mx-auto leading-relaxed">
                    Your website will render here in real-time as the AI generates code
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

export default WebsiteBuilderWorkspace;
